import { cn } from '@latitude/token-utils';

import type { Direction, SVGProps } from './types';

// State is stashed on globalThis so writers and readers see the same value
// even when they end up in different bundles. Storybook resolves
// `@latitude/icons` (used by preview.tsx and apps) to the published dist,
// while individual icon components import `./sprite-icon` from source via
// a relative path during the workspace's own build — those become two
// separate module instances after bundling, each with its own module-scope
// state. Consolidating onto globalThis keeps a single source of truth.
type IconsGlobals = {
  spriteUrl?: string;
};

const globals =
  (globalThis as unknown as { __latitudeIcons?: IconsGlobals })
    .__latitudeIcons ??
  ((
    globalThis as unknown as { __latitudeIcons?: IconsGlobals }
  ).__latitudeIcons = {});

// rslib (rspack) pre-evaluates process.env.* at library build time, so
// we can't embed the basePath via process.env in the dist. Instead, apps
// call configureSpriteUrl() from _app.tsx, which IS compiled by Next.js
// webpack and gets the correct process.env.PATH_PREFIX substitution.
const DEFAULT_SPRITE_URL = '/icon-sprite/sprite.svg';

/** Call once at app startup (e.g. in _app.tsx) to set the sprite URL. */
export const configureSpriteUrl = (url: string): void => {
  globals.spriteUrl = url;
};

const getSpriteUrl = (): string => globals.spriteUrl ?? DEFAULT_SPRITE_URL;

// Chevron in the design system points LEFT by default (chevron-left).
// Rotation maps from that baseline. Components using this with a direction
// prop other than "left" will rotate the whole <svg>.
const ROTATION_DEG: Record<Direction, number> = {
  left: 0,
  right: 180,
  up: 90,
  down: -90,
};

// Omit HTML `direction` attr (string | number | undefined) so callers can
// forward SVGProps cleanly without conflicting with our rotation prop.
type SpriteIconProps = Omit<SVGProps, 'direction'> & {
  /** Sprite symbol id (e.g. 'user-plus'). Looked up from SPRITE_MAPPING by callers. */
  spriteId: string;
  /** Rotation applied to the whole svg, mapped from the component's `direction` prop. */
  rotation?: Direction;
};

/**
 * Internal helper used by sprite-backed icon components in @latitude/icons.
 * Renders an svg + <use href> referencing the shared sprite.
 *
 * Consumers should not import this directly — use the named icon components
 * (e.g. `<GearIcon />`) or `<Icon name="..." />`.
 */
export const SpriteIcon = ({
  spriteId,
  title,
  rotation,
  className,
  style,
  ...rest
}: SpriteIconProps) => {
  // aria-hidden would override a caller's aria-label, so all three count as named.
  const isNamed = Boolean(
    title || rest['aria-label'] || rest['aria-labelledby'],
  );

  const rotationDeg = rotation ? ROTATION_DEG[rotation] : 0;
  const appliedStyle = rotationDeg
    ? { ...style, transform: `rotate(${rotationDeg}deg)` }
    : style;

  return (
    <svg
      // Match inline SVG defaults: viewBox + intrinsic size = 24×24.
      // The shared viewBox lets the <use>-referenced <symbol> (also
      // 0 0 24 24) scale 1:1, so consumer CSS classes like
      // `[&>svg]:w-X [&>svg]:h-X` resize sprite icons identically
      // to inline icons — no per-component :has(use) overrides needed.
      viewBox="0 0 24 24"
      width="24"
      height="24"
      className={cn('fill-current', className)}
      style={appliedStyle}
      // Keyed off the sprite symbol, not the title: titles vary per call site and
      // are absent on decorative icons. The `icon-` prefix avoids colliding with
      // six sprite ids already used verbatim as test ids on wrapper elements.
      // A caller's own data-testid wins, since {...rest} is spread after this.
      // No role="img" — adding it breaks getByRole('img') lookups for <img>.
      data-testid={`icon-${spriteId}`}
      aria-hidden={isNamed ? undefined : true}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <use href={`${getSpriteUrl()}#${spriteId}`} />
    </svg>
  );
};
